import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp, Search, Layers, Activity, BookOpen, Zap, Cpu, Clock, Shield
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ========== Money View: RAG Savings ==========
export function RAGSavingsCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/rag/summary`).then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  return (
    <Card className="border border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid="rag-savings-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-[var(--qn-foreground)] flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-emerald-500" />
          RAG Savings This Month
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-2xl font-bold font-mono text-emerald-600">${data.savings.net_savings_usd.toFixed(4)}</p>
            <p className="text-xs text-[var(--qn-foreground-subtle)] mt-0.5">Net Savings</p>
          </div>
          <div>
            <p className="text-lg font-bold font-mono text-[var(--qn-foreground)]">{data.savings.roi}</p>
            <p className="text-xs text-[var(--qn-foreground-subtle)] mt-0.5">ROI</p>
          </div>
          <div>
            <p className="text-lg font-bold font-mono text-[var(--qn-foreground)]">
              {data.knowledge_base.total_documents}
            </p>
            <p className="text-xs text-[var(--qn-foreground-subtle)] mt-0.5">
              {data.knowledge_base.total_chunks} chunks
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ========== Manager View: Retrieval Intelligence ==========
export function RetrievalIntelligenceCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/rag/summary`).then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  const strategies = data.retrieval?.strategies || {};
  const total = Object.values(strategies).reduce((a, b) => a + b, 0) || 1;
  const bars = [
    { key: "skip", label: "Skip", color: "bg-gray-400", count: strategies.skip || 0 },
    { key: "cache_only", label: "Cache", color: "bg-blue-400", count: strategies.cache_only || 0 },
    { key: "single_hop", label: "Single Hop", color: "bg-emerald-400", count: strategies.single_hop || 0 },
    { key: "multi_hop", label: "Multi Hop", color: "bg-violet-400", count: strategies.multi_hop || 0 },
    { key: "decomposed", label: "Decomposed", color: "bg-amber-400", count: strategies.decomposed || 0 },
  ];

  return (
    <Card className="border border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid="retrieval-intelligence-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-[var(--qn-foreground)] flex items-center gap-2">
          <Search className="w-4 h-4 text-violet-500" />
          Retrieval Intelligence
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Strategy bar */}
        <div className="flex h-4 rounded-full overflow-hidden bg-[var(--qn-surface)]">
          {bars.filter(b => b.count > 0).map((b) => (
            <div key={b.key} className={`${b.color}`} style={{ width: `${(b.count / total) * 100}%` }}
              title={`${b.label}: ${b.count}`} />
          ))}
        </div>
        <div className="flex flex-wrap gap-3">
          {bars.map((b) => (
            <div key={b.key} className="flex items-center gap-1 text-[11px] text-[var(--qn-foreground-muted)]">
              <div className={`w-2 h-2 rounded-sm ${b.color}`} />{b.label}: {b.count}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3 pt-1 border-t border-[var(--qn-border)]">
          <div>
            <p className="text-xs text-[var(--qn-foreground-subtle)]">Cache Hit Rate</p>
            <p className="text-sm font-mono font-medium text-[var(--qn-foreground)]">{data.retrieval.cache_hit_rate}</p>
          </div>
          <div>
            <p className="text-xs text-[var(--qn-foreground-subtle)]">Avg Context Injection</p>
            <p className="text-sm font-mono font-medium text-[var(--qn-foreground)]">
              {data.retrieval.total_context_tokens} tokens
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ========== Engineer View: RAG Pipeline ==========
export function RAGPipelineCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/rag/summary`).then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  const policy = data.policy || {};
  const latency = data.retrieval?.avg_search_latency_ms || 0;

  return (
    <Card className="border border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid="rag-pipeline-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-[var(--qn-foreground)] flex items-center gap-2">
          <Cpu className="w-4 h-4 text-cyan-500" />
          RAG Pipeline
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Latency breakdown */}
        <div>
          <p className="text-xs text-[var(--qn-foreground-subtle)] mb-2">Pipeline Latency</p>
          <div className="flex items-center gap-1 h-5">
            <div className="bg-blue-400 h-full rounded-l" style={{ width: "30%" }} title="Embedding" />
            <div className="bg-emerald-400 h-full" style={{ width: "50%" }} title="Search" />
            <div className="bg-amber-400 h-full rounded-r" style={{ width: "20%" }} title="Assembly" />
          </div>
          <div className="flex justify-between mt-1 text-[10px] text-[var(--qn-foreground-subtle)]">
            <span>Embed ~{Math.round(latency * 0.3)}ms</span>
            <span>Search ~{Math.round(latency * 0.5)}ms</span>
            <span>Assemble ~{Math.round(latency * 0.2)}ms</span>
          </div>
        </div>

        {/* Policy params */}
        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[var(--qn-border)]">
          <div>
            <p className="text-xs text-[var(--qn-foreground-subtle)]">Similarity Threshold</p>
            <p className="text-sm font-mono font-medium text-[var(--qn-foreground)]">{policy.similarity_threshold ?? "default"}</p>
          </div>
          <div>
            <p className="text-xs text-[var(--qn-foreground-subtle)]">Context Ratio</p>
            <p className="text-sm font-mono font-medium text-[var(--qn-foreground)]">{policy.max_context_ratio ?? "default"}</p>
          </div>
          <div>
            <p className="text-xs text-[var(--qn-foreground-subtle)]">Policy Source</p>
            <div className="text-sm font-medium text-[var(--qn-foreground)]">
              <Badge variant="outline" className="text-[10px]">{policy.optimization_source || "defaults"}</Badge>
            </div>
          </div>
          <div>
            <p className="text-xs text-[var(--qn-foreground-subtle)]">Total Retrievals</p>
            <p className="text-sm font-mono font-medium text-[var(--qn-foreground)]">{data.retrieval.total_retrievals}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
